import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Volume2, VolumeX } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { API } from "@/lib/api";

const sessGet = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };
const sessSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } };
// Files uploaded in another environment share the same storage: serve them from the current backend
const localize = (u) => (u && u.includes("/api/files/") ? `${API}/files/${u.split("/api/files/")[1]}` : u);

// Delicate, melodic lounge loop generated with Web Audio (fallback when no MP3 is uploaded)
const CHORDS = [
  { pad: [220.0, 261.63, 329.63, 392.0], arp: [440.0, 523.25, 659.25, 783.99, 659.25, 523.25] },   // Am7
  { pad: [174.61, 220.0, 261.63, 329.63], arp: [349.23, 440.0, 523.25, 659.25, 523.25, 440.0] },   // Fmaj7
  { pad: [261.63, 329.63, 392.0, 493.88], arp: [523.25, 659.25, 783.99, 987.77, 783.99, 659.25] }, // Cmaj7
  { pad: [196.0, 246.94, 293.66, 369.99], arp: [392.0, 493.88, 587.33, 739.99, 587.33, 493.88] },  // G maj7
];

function startEngine(ctx, master) {
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1800; lp.Q.value = 0.5;
  const delay = ctx.createDelay(3); delay.delayTime.value = 0.6;
  const fb = ctx.createGain(); fb.gain.value = 0.42;
  const wet = ctx.createGain(); wet.gain.value = 0.5;
  delay.connect(fb); fb.connect(delay); delay.connect(wet); wet.connect(master);
  lp.connect(master); lp.connect(delay);

  const barLen = 8;
  let i = 0;
  const note = (f, t, dur, peak, type = "sine") => {
    const o = ctx.createOscillator(); o.type = type; o.frequency.value = f;
    const g = ctx.createGain(); g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    o.connect(g); g.connect(lp); o.start(t); o.stop(t + dur + 0.05);
  };
  const schedule = () => {
    const t = ctx.currentTime + 0.05;
    const { pad, arp } = CHORDS[i % CHORDS.length]; i++;
    pad.forEach((f, k) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f; o.detune.value = (k - 1.5) * 3;
      const g = ctx.createGain(); g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.035, t + 2.5);
      g.gain.setValueAtTime(0.035, t + barLen - 2.5);
      g.gain.linearRampToValueAtTime(0, t + barLen);
      o.connect(g); g.connect(lp); o.start(t); o.stop(t + barLen + 0.1);
    });
    const step = barLen / 8;
    arp.forEach((f, k) => note(f, t + k * step + (k % 2) * 0.03, 2.2, 0.075, "triangle"));
    note(arp[0] * 2, t + 6 * step, 2.4, 0.045);
    note(arp[2] * 2, t + 7 * step, 2.4, 0.04);
  };
  schedule();
  return setInterval(schedule, barLen * 1000);
}

export default function AmbientMusic() {
  const { settings } = useSite();
  const { pathname } = useLocation();
  const [muted, setMuted] = useState(() => sessGet("afrodite_music_muted") === "true");
  const [playing, setPlaying] = useState(false);
  const ref = useRef({});
  const audioRef = useRef(null);
  const musicUrl = localize(settings.music_url || "");
  const hidden = pathname.startsWith("/admin") || pathname.startsWith("/forgot-password") || pathname.startsWith("/reset-password");

  const start = () => {
    if (playing && !muted) return;
    if (musicUrl) {
      const a = audioRef.current; if (!a) return;
      a.volume = 0; a.play().then(() => {
        setPlaying(true);
        let v = 0; const id = setInterval(() => { v = Math.min(0.6, v + 0.03); try { a.volume = v; } catch { /* iOS */ } if (v >= 0.6) clearInterval(id); }, 150);
      }).catch(() => { setPlaying(false); });
      return;
    }
    if (ref.current.ctx) { ref.current.ctx.resume(); setPlaying(true); return; }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const master = ctx.createGain(); master.gain.setValueAtTime(0, ctx.currentTime);
    master.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 5);
    master.connect(ctx.destination);
    ref.current = { ctx, master, timer: startEngine(ctx, master) };
    setPlaying(true);
  };

  const stop = () => {
    ref.current.ctx?.suspend();
    audioRef.current?.pause();
    setPlaying(false);
  };

  useEffect(() => {
    if (muted || hidden) return;
    const kick = () => { start(); };
    window.addEventListener("pointerdown", kick, { capture: true });
    window.addEventListener("touchstart", kick, { capture: true, passive: true });
    window.addEventListener("keydown", kick, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", kick, { capture: true });
      window.removeEventListener("touchstart", kick, { capture: true });
      window.removeEventListener("keydown", kick, { capture: true });
    };
  }, [muted, hidden, musicUrl, playing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (hidden) stop(); }, [hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { clearInterval(ref.current.timer); ref.current.ctx?.close(); }, []);

  const toggle = () => {
    if (!muted && !playing) { start(); return; }
    const next = !muted; setMuted(next); sessSet("afrodite_music_muted", String(next));
    if (next) stop(); else start();
  };

  return (
    <>
      {musicUrl && <audio ref={audioRef} src={musicUrl} loop preload="auto" data-testid="music-audio" />}
      {!hidden && (
        <button onClick={toggle} data-testid="music-toggle-btn" aria-label={muted ? "Attiva musica" : "Silenzia musica"} title={muted ? "Attiva musica" : "Silenzia musica"}
          className={`fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-40 h-11 rounded-full glass border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/15 flex items-center justify-center gap-2 transition-colors ${playing && !muted ? "w-11" : "px-4"}`}>
          {muted || !playing ? <><VolumeX className="w-4 h-4" /><span className="text-xs tracking-wide">{muted ? "Musica off" : "Tocca per la musica"}</span></> : <Volume2 className="w-4 h-4 animate-pulse" />}
        </button>
      )}
    </>
  );
}
